import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, Input } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import { IconPlus } from '@arco-design/web-react/icon';
import { get } from 'lodash';

import { SectionHeader } from '@/components/SectionHeader';
import { MAAS_INPUT_PLACEHOLDER, maasRequiredRules } from '@/delivery-type-plugin/capabilities/maas-init/form-messages';
import { DEFAULT_MAAS_OPTIONS, type MaasOptions } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { getModelSizeGi } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelFormUtils';
import type { MaasModel, ModelInstanceFormValues } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { parseStorageQuantityToGi } from '@/delivery-type-plugin/capabilities/maas-model-storage/quantity';
import {
  MODEL_DATA_VOLUME_NAME,
  getVolumeClaimDisplayName,
  getVolumeClaimName,
  getVolumePvcTemplate,
  getVolumeStorageClassInfo,
} from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import { MaasDataDiskMode, getDataDiskMode } from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { useResourceSectionReadOnly } from '@/delivery-type-plugin/components';
import {
  MODEL_DATA_DISK_ACCESS_MODES,
  type MaasDataDiskCardItem,
  MaasDataDiskCreateModal,
  MaasDataDiskItems,
  MaasDataDiskSelectModal,
} from '@/delivery-type-plugin/components/maas-data-disk';
import { usePersistentVolumeClaims } from '@/delivery-type-plugin/hooks/usePersistentVolumeClaims';
import { useOptionalResourceFormContext } from '@/delivery-type-plugin/platform/resource-form';

import { hasPresetModelStorage } from './hasPresetModelStorage';
import { MaasStorageSettingMulti } from './maas-storage-setting-multi';
import styles from './maas-storage-setting.module.less';
import { ModelCategoryFormItem } from './model-category-field';
import { MaasModelSummaryCard } from './model-info-card';
import { getCreateStorageModalModelRules, validateStorageModalModelFields } from './model-source-form-rules';
import ModelSourceSetting from './model-source-setting';
import sourceStyles from './model-source-setting.module.less';
import { useMaasStorageState } from './useMaasStorageState';

const FormItem = Form.Item;

interface MaasStorageSettingProps {
  form: FormInstance;
  /** SKU 外层表单，用于数据盘弹窗确认后触发 DataVolume 算价 */
  skuOuterForm?: FormInstance;
  typeOptions?: MaasOptions;
  /** 编辑/URL 带入的初始值，用于卡片展示（model.* / _primaryDataVolume 可能未挂载 Form.Item） */
  initValues?: ModelInstanceFormValues;
  /** 模型仓库跳转：仅展示「创建数据盘」，隐藏「添加已有数据盘」 */
  createStorageOnly?: boolean;
  isEdit?: boolean;
  /** SKU pages context 与 platform 非同实例时需显式传入 */
  billModelId?: string;
  /** 交付类型 id，计价 resolve 必传 */
  deploymentTypeId?: string;
  paymentTiming?: string;
  skuType?: string;
  resourceEditMode?: string;
  /** 下单：SKU 预置已有数据盘时禁止删改 */
  lockActionsWhenFilled?: boolean;
  resourceCode?: string;
  onPriceChange?: (outerValues: Record<string, unknown>, innerForm: FormInstance) => void | Promise<void>;
  modelInstanceMultiSource?: boolean;
}

/** 旧版：模型与存储合并弹窗 */
function MaasStorageSettingLegacy({
  form,
  skuOuterForm,
  typeOptions = DEFAULT_MAAS_OPTIONS,
  initValues,
  createStorageOnly,
  isEdit,
  billModelId: billModelIdProp,
  deploymentTypeId: deploymentTypeIdProp,
  paymentTiming,
  skuType,
  resourceEditMode,
  lockActionsWhenFilled: lockActionsWhenFilledProp,
  resourceCode = 'DataVolume',
  onPriceChange,
}: Omit<MaasStorageSettingProps, 'modelInstanceMultiSource'>) {
  const { t } = useTranslation();
  const sectionReadOnly = useResourceSectionReadOnly();
  const resourceFormCtx = useOptionalResourceFormContext();
  /** props 优先：SKU 页 ResourceFormProvider 与 platform context 非同实例 */
  const billModelId = billModelIdProp ?? resourceFormCtx?.billModelId;
  const deploymentTypeId = deploymentTypeIdProp ?? resourceFormCtx?.deploymentTypeId;
  const resolvedPaymentTiming = paymentTiming ?? resourceFormCtx?.paymentTiming;
  const resolvedSkuType = skuType ?? resourceFormCtx?.skuType;
  const resolvedResourceEditMode = resourceEditMode ?? resourceFormCtx?.resourceEditMode;
  /** 固定规格下单：无论是否有数据均禁止增删改 */
  const isOrderFixed = resolvedResourceEditMode === 'order-fixed';
  const lockActionsWhenFilled = lockActionsWhenFilledProp === true;
  const locked = sectionReadOnly || isOrderFixed;
  const { options: pvcOptions } = usePersistentVolumeClaims({
    resources_filter: undefined,
    status: 'available',
  });
  const pvcDisplayNameByClaim = useMemo(
    () => Object.fromEntries(pvcOptions.map((item) => [item.value, item.label || item.value])),
    [pvcOptions],
  );

  const volumeToCardItem = useCallback(
    (volume: Record<string, unknown>): MaasDataDiskCardItem => {
      const mode = getDataDiskMode(volume) || MaasDataDiskMode.Existing;
      if (mode === MaasDataDiskMode.Create) {
        const template = getVolumePvcTemplate(volume);
        const storageRaw = get(template, 'resources.requests.storage');
        const storageGi = parseStorageQuantityToGi(storageRaw);
        return {
          mode,
          name: String(volume.name || t('components:create.data.disk')),
          storageClassInfo: getVolumeStorageClassInfo(volume),
          storageGi: storageGi ?? (storageRaw !== undefined && storageRaw !== null ? String(storageRaw) : undefined),
          accessModes: get(template, 'accessModes') as string[] | undefined,
          reclaimPolicy: volume.reclaimPolicy ? String(volume.reclaimPolicy) : undefined,
        };
      }
      const claimName = getVolumeClaimName(volume);
      const displayName =
        getVolumeClaimDisplayName(volume) || (claimName ? pvcDisplayNameByClaim[claimName] : undefined) || claimName || '';
      return {
        mode,
        name: displayName,
        reclaimPolicy: volume.reclaimPolicy ? String(volume.reclaimPolicy) : undefined,
        claimName,
      };
    },
    [pvcDisplayNameByClaim, t],
  );

  const modelSizeGi = getModelSizeGi(Form.useWatch('model', form));
  const watchedModel = Form.useWatch('model', form) as MaasModel | undefined;
  const modelSizeBytes = modelSizeGi ? Math.ceil(modelSizeGi * 1024 ** 3) : undefined;
  const modelMountPath = Form.useWatch('model.mountPath', form) as string | undefined;

  const createStorageModalModelRules = useMemo(() => getCreateStorageModalModelRules(form), [form]);
  const {
    createModalOpen,
    selectModalOpen,
    editingVolume,
    initModelSnapshot,
    dataVolume,
    hasDataDisk,
    dataDiskMode,
    hasModelPrefill,
    handleSaveVolume,
    handleSaveExistingVolume,
    handleDelete,
    openCreateModal,
    openSelectModal,
    openEditModal,
    closeCreateModal,
    closeSelectModal,
  } = useMaasStorageState({
    form,
    initValues,
    modelMountPath,
    watchedModel,
    createStorageOnly,
  });

  const cardItem = useMemo(
    () => (hasDataDisk && dataVolume ? volumeToCardItem(dataVolume) : undefined),
    [hasDataDisk, dataVolume, volumeToCardItem],
  );

  /** 下单自定义/后付费：SKU 预置已有模型与存储时禁止删改（用户下单新增不锁） */
  const presetHasDataDisk = useMemo(() => hasPresetModelStorage(initValues as Record<string, unknown> | undefined), [initValues]);
  const actionsLockedByFill = lockActionsWhenFilled && presetHasDataDisk;
  const canMutateCard = !locked && !actionsLockedByFill && !isEdit;

  const createModalBeforeContent = (
    <Form form={form} layout="vertical">
      <FormItem
        label={t('data-center:maas.model.select')}
        field="model.source"
        required
        rules={createStorageModalModelRules}
        className={sourceStyles.modelSourceField}
      >
        <ModelSourceSetting form={form} typeOptions={typeOptions} />
      </FormItem>
      <FormItem label={t('data-center:maas.service.name')} field="model.name" rules={maasRequiredRules(t('data-center:maas.service.name'))}>
        <Input placeholder={MAAS_INPUT_PLACEHOLDER} />
      </FormItem>
      <ModelCategoryFormItem />
      <FormItem label={t('data-center:maas.model.size')} field="model.size" rules={maasRequiredRules(t('data-center:maas.model.size'))}>
        <Input placeholder="10Gi" />
      </FormItem>
    </Form>
  );

  const selectModalAfterContent = (
    <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
      <FormItem label={t('data-center:maas.service.name')} field="model.name" rules={maasRequiredRules(t('data-center:maas.service.name'))}>
        <Input placeholder={MAAS_INPUT_PLACEHOLDER} />
      </FormItem>
      <ModelCategoryFormItem />
      <FormItem label={t('data-center:maas.model.size')} field="model.size" rules={maasRequiredRules(t('data-center:maas.model.size'))}>
        <Input placeholder="10Gi" />
      </FormItem>
    </Form>
  );

  const validateCreateModalModelFields = useCallback(() => validateStorageModalModelFields(form, true), [form]);
  const validateSelectModalModelFields = useCallback(() => validateStorageModalModelFields(form, false), [form]);

  const msg = {
    // 已默认选中/预填模型时，入口文案改为「模型存储配置」
    add: createStorageOnly
      ? t('data-center:maas.storage.configure')
      : hasModelPrefill
        ? t('data-center:maas.model.storage.configure')
        : t('data-center:maas.model.storage.add.new'),
    add_existing: t('data-center:maas.model.storage.add.existing'),
  };

  return (
    <div className={styles.root}>
      <MaasDataDiskItems
        item={cardItem}
        onEdit={canMutateCard && hasDataDisk ? openEditModal : undefined}
        onDelete={canMutateCard && hasDataDisk ? handleDelete : undefined}
        isEdit={isEdit || locked || actionsLockedByFill}
      />
      {!hasDataDisk && !locked && (
        <div className={styles.addActions}>
          <div className={styles.addActionRow}>
            <SectionHeader inline title={msg.add} />
            <Button icon={<IconPlus />} type="primary" onClick={openCreateModal} />
          </div>
          {!createStorageOnly ? (
            <div className={styles.addActionRow}>
              <SectionHeader inline title={msg.add_existing} />
              <Button icon={<IconPlus />} type="primary" onClick={openSelectModal} />
            </div>
          ) : null}
        </div>
      )}
      {hasDataDisk && dataDiskMode ? (
        <MaasModelSummaryCard
          form={form}
          mode={dataDiskMode}
          initModel={initModelSnapshot}
          initModelCategory={initValues?._modelCategory}
          onEdit={canMutateCard ? openEditModal : () => undefined}
        />
      ) : hasModelPrefill ? (
        <MaasModelSummaryCard
          form={form}
          mode={MaasDataDiskMode.Create}
          initModel={initModelSnapshot}
          initModelCategory={initValues?._modelCategory}
          onEdit={canMutateCard ? (hasDataDisk ? openEditModal : openCreateModal) : () => undefined}
        />
      ) : null}
      <MaasDataDiskCreateModal
        open={createModalOpen}
        title={msg.add}
        initVolume={editingVolume && getDataDiskMode(editingVolume) === MaasDataDiskMode.Create ? editingVolume : undefined}
        modelSizeBytes={modelSizeBytes}
        defaultMountPath={modelMountPath || '/models'}
        volumeName={MODEL_DATA_VOLUME_NAME}
        hideMountPath
        fixedAccessModes={MODEL_DATA_DISK_ACCESS_MODES}
        beforeContent={createModalBeforeContent}
        onValidateExtra={validateCreateModalModelFields}
        billModelId={billModelId}
        deploymentTypeId={deploymentTypeId}
        resourceCode={resourceCode}
        paymentTiming={resolvedPaymentTiming}
        skuType={resolvedSkuType}
        resourceEditMode={resolvedResourceEditMode}
        skuOuterForm={skuOuterForm}
        resourceInnerForm={form}
        onPriceChange={onPriceChange}
        onClose={closeCreateModal}
        onSave={handleSaveVolume}
      />
      {!createStorageOnly ? (
        <MaasDataDiskSelectModal
          open={selectModalOpen}
          title={msg.add_existing}
          filterByModelSize={false}
          initVolume={editingVolume && getDataDiskMode(editingVolume) === MaasDataDiskMode.Existing ? editingVolume : undefined}
          defaultMountPath={modelMountPath || '/models'}
          volumeName={MODEL_DATA_VOLUME_NAME}
          hideMountPath
          afterContent={selectModalAfterContent}
          onValidateExtra={validateSelectModalModelFields}
          resourceEditMode={resolvedResourceEditMode}
          onClose={closeSelectModal}
          onSave={handleSaveExistingVolume}
        />
      ) : null}
    </div>
  );
}

/** 模型实例存储：多源或旧版合并弹窗 */
export function MaasStorageSetting(props: MaasStorageSettingProps) {
  if (props.modelInstanceMultiSource) {
    return <MaasStorageSettingMulti {...props} />;
  }
  return <MaasStorageSettingLegacy {...props} />;
}

export default MaasStorageSetting;
